import { theme as antdTheme } from 'antd';
import type { ThemeConfig } from 'antd';

/**
 * SSP Enterprise Theme Configuration
 *
 * Uses Ant Design token-based theming with the built-in algorithms as base:
 * defaultAlgorithm (light) / darkAlgorithm (dark) derive all secondary tokens
 * (hover/active states, semantic backgrounds, control tints), with our stone
 * palette overriding the surfaces. Colors match ssp-relay-dashboard and
 * sspwallet.io brand.
 *
 * Color Palette:
 * - Primary: Amber #fbbf24 (HSL 43 96% 56%)
 * - Dark Background: #0c0a09 (HSL 20 14.3% 4.1%)
 * - Dark Border: #272524 (HSL 12 6.5% 15.1%)
 * - Dark Text: #fafaf9 (HSL 60 9.1% 97.8%)
 * - Dark Text Secondary: #a8a29e (HSL 24 5.4% 63.9%)
 */

// Shared tokens
const sharedTokens = {
  colorPrimary: '#fbbf24',
  colorSuccess: '#22c55e',
  colorWarning: '#f59e0b',
  colorError: '#ef4444',
  colorInfo: '#3b82f6',
  borderRadius: 8,
  borderRadiusLG: 12,
  borderRadiusSM: 6,
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  fontSize: 14,
  // Motion
  motionDurationFast: '0.1s',
  motionDurationMid: '0.15s',
  motionDurationSlow: '0.3s',
};

export const lightTheme: ThemeConfig = {
  algorithm: antdTheme.defaultAlgorithm,
  token: {
    ...sharedTokens,
    // Base colors
    colorTextBase: '#1c1917',
    colorBgBase: '#fafaf9',
    // Background
    colorBgContainer: '#ffffff',
    colorBgElevated: '#ffffff',
    colorBgLayout: '#fafaf9',
    colorBgSpotlight: '#f5f5f4',
    // Border
    colorBorder: '#e7e5e4',
    colorBorderSecondary: '#d6d3d1',
    // Text
    colorText: '#1c1917',
    colorTextSecondary: '#57534e',
    colorTextTertiary: '#78716c',
    colorTextQuaternary: '#a8a29e',
    // Fill (for backgrounds of interactive elements)
    colorFill: '#f5f5f4',
    colorFillSecondary: '#e7e5e4',
    colorFillTertiary: '#d6d3d1',
    colorFillQuaternary: '#fafaf9',
    // Split (dividers)
    colorSplit: '#e7e5e4',
  },
  components: {
    Button: {
      primaryColor: '#000000',
      algorithm: true,
      controlHeight: 40,
      paddingContentHorizontal: 20,
    },
    Layout: {
      siderBg: '#ffffff',
      headerBg: '#ffffff',
      bodyBg: '#fafaf9',
      headerPadding: '0 24px',
      headerHeight: 64,
    },
    Card: {
      paddingLG: 24,
    },
    Modal: {
      contentBg: '#ffffff',
      headerBg: 'transparent',
      titleFontSize: 18,
      borderRadiusLG: 16,
    },
    Input: {
      colorBgContainer: '#f5f5f4',
      activeBorderColor: '#fbbf24',
      hoverBorderColor: '#d6d3d1',
      controlHeight: 40,
    },
    Select: {
      controlHeight: 40,
    },
    Table: {
      headerBg: '#f5f5f4',
      headerColor: '#57534e',
      rowHoverBg: '#fafaf9',
    },
    Tag: {
      borderRadiusSM: 6,
    },
    Alert: {
      borderRadiusLG: 8,
    },
    Message: {
      contentBg: '#ffffff',
    },
    Notification: {
      width: 384,
    },
    Tooltip: {
      colorBgSpotlight: '#1c1917',
      colorTextLightSolid: '#fafaf9',
    },
    Dropdown: {
      paddingBlock: 8,
      borderRadiusLG: 12,
    },
    Statistic: {
      titleFontSize: 13,
      contentFontSize: 28,
    },
    Progress: {
      // Brand amber instead of antd's default info-blue — signature progress
      // is a signing surface (DESIGN_TOKENS.md brand anchor).
      defaultColor: '#fbbf24',
    },
  },
};

export const darkTheme: ThemeConfig = {
  algorithm: antdTheme.darkAlgorithm,
  token: {
    ...sharedTokens,
    // Base colors - matching relay dashboard exactly
    colorTextBase: '#fafaf9',
    colorBgBase: '#0c0a09',
    // Background - all same color (relay dashboard pattern)
    colorBgContainer: '#0c0a09',
    colorBgElevated: '#0c0a09',
    colorBgLayout: '#0c0a09',
    colorBgSpotlight: '#1a1918',
    // Border
    colorBorder: '#272524',
    colorBorderSecondary: '#3d3a38',
    // Text
    colorText: '#fafaf9',
    colorTextSecondary: '#a8a29e',
    colorTextTertiary: '#78716c',
    colorTextQuaternary: '#57534e',
    // Fill
    colorFill: '#272524',
    colorFillSecondary: '#1a1918',
    colorFillTertiary: '#141312',
    colorFillQuaternary: '#0f0e0d',
    // Split
    colorSplit: '#272524',
  },
  components: {
    Button: {
      primaryColor: '#000000',
      algorithm: true,
      controlHeight: 40,
      paddingContentHorizontal: 20,
    },
    Layout: {
      siderBg: '#0c0a09',
      headerBg: '#0c0a09',
      bodyBg: '#0c0a09',
      headerPadding: '0 24px',
      headerHeight: 64,
    },
    Card: {
      paddingLG: 24,
    },
    Modal: {
      contentBg: '#0c0a09',
      headerBg: 'transparent',
      titleFontSize: 18,
      titleColor: '#fafaf9',
      borderRadiusLG: 16,
    },
    Input: {
      colorBgContainer: 'transparent',
      colorBorder: '#272524',
      activeBorderColor: '#fbbf24',
      hoverBorderColor: '#3d3a38',
      controlHeight: 40,
    },
    Select: {
      controlHeight: 40,
      optionSelectedBg: 'rgba(251, 191, 36, 0.16)',
      optionActiveBg: 'rgba(255, 255, 255, 0.06)',
      optionSelectedFontWeight: 600,
    },
    Table: {
      headerBg: '#272524',
      headerColor: '#a8a29e',
      rowHoverBg: '#1a1918',
      rowSelectedBg: 'rgba(251, 191, 36, 0.16)',
      rowSelectedHoverBg: 'rgba(251, 191, 36, 0.22)',
      colorBgContainer: 'transparent',
    },
    Dropdown: {
      colorBgElevated: '#0c0a09',
      paddingBlock: 8,
      borderRadiusLG: 12,
    },
    Tag: {
      borderRadiusSM: 6,
    },
    Alert: {
      borderRadiusLG: 8,
    },
    Message: {
      contentBg: '#1a1918',
    },
    Notification: {
      width: 384,
      colorBgElevated: '#0c0a09',
    },
    Tooltip: {
      colorBgSpotlight: '#272524',
      colorTextLightSolid: '#fafaf9',
    },
    Steps: {
      colorPrimary: '#fbbf24',
    },
    Progress: {
      // Brand amber instead of antd's default info-blue — signature progress
      // is a signing surface (DESIGN_TOKENS.md brand anchor).
      defaultColor: '#fbbf24',
    },
    Statistic: {
      titleFontSize: 13,
      contentFontSize: 28,
    },
  },
};
